<template>
	<Skeleton :paragraph="{ rows: 7 }" :title="false" :loading="isLoading">
		<VerticalLayout>
			<RadioGroup
				v-model:value="daysValue"
				:options="daysOptions"
				option-type="button"
				@change="onDaysChange"
			/>
			<div class="scrollable-x">
				<Table
					:columns="columns"
					:data-source="items"
					:row-key="record => record.process_label + '-' + record.pid"
					:pagination="{ defaultPageSize: 10 }"
					size="small"
				>
					<template #bodyCell="{ column, record }">
						<template v-if="column.dataIndex === 'memory_bytes'">
							{{ formatBytes(record.memory_bytes) }}
						</template>
						<template v-else-if="column.dataIndex === 'process_label'">
							<TypographyText :type="record.is_active ? 'primary' : 'secondary'">{{ record.process_label }}</TypographyText>
						</template>
					</template>
					<template #emptyText>
						{{ $t('settings.processMemory.noData') }}
					</template>
				</Table>
			</div>
			<div v-if="showChart">
				<TimeSeriesChart
					:isLoading="isLoading"
					:series="chartSeries"
					:options="chartOptions"
					:height="600"
				/>
			</div>
		</VerticalLayout>
	</Skeleton>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { Skeleton, Table, TypographyText, RadioGroup } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';

import { getBaseOptions, generateSeries, setXAxisRange, nullSafeFormatter } from '@/utils/chartsUtils';
import { roundNativeDate } from '@/components/common/dateUtils';

import VerticalLayout from '@/components/common/VerticalLayout';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart';

export default {
	components: {
		Skeleton, Table, TypographyText, RadioGroup, VerticalLayout, TimeSeriesChart,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(false);
		const items = ref([]);
		const history = ref([]);
		const columns = Consts.processMemoryTableColumnsComp;
		const daysValue = ref(1);
		const daysOptions = computed(() => [
			{ label: t('settings.performance.daysOptions.day1'), value: 1 },
			{ label: t('settings.performance.daysOptions.week1'), value: 7 },
		]);

		const formatBytes = bytes => {
			if (bytes == null) return '-';
			const mbytes = bytes / (1024 * 1024);
			return mbytes.toFixed(2);
		};

		const getSeriesKey = item => item.process_label + ' (' + item.pid + ')';

		const chartDaysMs = computed(() => daysValue.value * 24 * 60 * 60 * 1000);

		const chartDateRange = computed(() => {
			const maxDate = roundNativeDate(new Date(), 'minute', 10);
			if (!history.value.length) {
				return {
					minDate: roundNativeDate(new Date(maxDate.getTime() - chartDaysMs.value), 'minute', 10),
					maxDate,
				};
			}
			const minTimestamp = Math.min(...history.value.map(item => new Date(item.collected_at).getTime()));
			return {
				minDate: roundNativeDate(new Date(Math.max(minTimestamp, maxDate.getTime() - chartDaysMs.value)), 'minute', 10),
				maxDate,
			};
		});

		const chartSeries = computed(() => {
			if (!history.value.length) {
				return [];
			}
			const forceSeriesKeys = items.value.map(getSeriesKey);
			return generateSeries({
				items: history.value,
				datetimeField: 'collected_at',
				getSeriesKeyFn: getSeriesKey,
				getValueFn: item => item.memory_bytes / (1024 * 1024),
				seriesStep: 10,
				seriesStepUnit: 'minute',
				showNullAsZero: true,
				minDate: chartDateRange.value.minDate,
				maxDate: chartDateRange.value.maxDate,
				roundTimeValues: true,
				forceSeriesKeys,
			}).series;
		});

		const chartOptions = computed(() => {
			const options = getBaseOptions('processMemory', chartSeries.value);
			options.chart.zoom = { allowMouseWheelZoom: false };
			options.yaxis.title.text = 'megabytes';
			options.tooltip.y.formatter = nullSafeFormatter(value => formatBytes(value * 1024 * 1024) + ' MB');
			setXAxisRange(options, [chartDateRange.value.minDate, chartDateRange.value.maxDate]);
			return options;
		});

		const showChart = computed(() => chartSeries.value.length > 0);

		const fetchData = () => {
			isLoading.value = true;
			Api.utilsProcessMemoryGet(daysValue.value, response => {
				if (response.status === 'ok') {
					items.value = response.items || [];
					history.value = response.history || [];
				}
				isLoading.value = false;
			});
		};

		const onDaysChange = () => {
			fetchData();
		};

		onMounted(fetchData);

		return {
			isLoading,
			items,
			history,
			columns,
			daysValue,
			daysOptions,
			onDaysChange,
			formatBytes,
			chartSeries,
			chartOptions,
			showChart,
		};
	},
}
</script>
